import qrcode from 'qrcode-generator';

export type QrLevel = 'L' | 'M' | 'Q' | 'H';

/** A logo drawn in the middle of the code (needs level H so the code still scans). */
export interface QrLogo {
  /** The logo's own square coordinate system is 0..size. */
  size: number;
  /** SVG path data of the logo. */
  path: string;
  fill: string;
}

export interface QrOptions {
  level?: QrLevel;
  /** Blank modules around the code; scanners need at least 4. */
  quiet?: number;
  fg?: string;
  bg?: string;
  /** Pixel size of the SVG's width and height; omitted, it scales to its container. */
  size?: number;
  logo?: QrLogo;
}

/** The dark modules of the QR code for `text`, row by row. */
export function qrModules(text: string, level: QrLevel = 'M'): boolean[][] {
  const qr = qrcode(0, level);
  qr.addData(text, 'Byte');
  qr.make();
  const n = qr.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => qr.isDark(r, c)));
}

/** Share of the code's width a centred logo may cover; level H survives losing about a fifth of the area. */
const LOGO_SHARE = 0.2;

/**
 * The QR code as an SVG document: one path for all dark modules (runs merged),
 * sharp at any size. With a logo the code uses error correction level H.
 */
export function qrSvg(text: string, { level, quiet = 4, fg = '#000000', bg = '#ffffff', size, logo }: QrOptions = {}): string {
  const modules = qrModules(text, logo ? 'H' : (level ?? 'M'));
  const n = modules.length;
  const total = n + quiet * 2;
  let d = '';
  modules.forEach((row, y) => {
    for (let x = 0; x < n; ) {
      if (!row[x]) {
        x++;
        continue;
      }
      let run = 1;
      while (x + run < n && row[x + run]) run++;
      d += `M${x + quiet} ${y + quiet}h${run}v1h-${run}z`;
      x += run;
    }
  });
  const dims = size ? ` width="${size}" height="${size}"` : '';
  let body = `<rect width="${total}" height="${total}" fill="${bg}"/><path d="${d}" fill="${fg}"/>`;
  if (logo) {
    // An odd number of modules keeps the logo centred on the module grid; a pad of background keeps it readable.
    const box = Math.floor((n * LOGO_SHARE) / 2) * 2 + 1;
    const at = (total - box) / 2;
    const pad = 0.8;
    const inner = box - pad * 2;
    const scale = inner / logo.size;
    body +=
      `<rect x="${at}" y="${at}" width="${box}" height="${box}" rx="${box * 0.18}" fill="${bg}"/>` +
      `<path transform="translate(${at + pad} ${at + pad}) scale(${scale})" d="${logo.path}" fill="${logo.fill}" fill-rule="evenodd"/>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}"${dims} shape-rendering="crispEdges" role="img" aria-label="QR code">${body}</svg>`;
}

/** An SVG as a data URL, for <img> or an image decoder. */
export const svgDataUrl = (svg: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/** Renders the QR code to a PNG of `px` by `px` pixels. */
export async function qrPng(text: string, px: number, options: Omit<QrOptions, 'size'> = {}): Promise<Blob> {
  const img = new Image();
  img.src = svgDataUrl(qrSvg(text, { ...options, size: px }));
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.drawImage(img, 0, 0, px, px);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('png failed'))), 'image/png'));
}

/** Reads the logo path out of the brand mark SVG (public/brand/logo-mark.svg). */
export function parseLogo(svg: string): QrLogo | null {
  const box = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg);
  const path = /<path[^>]*\sd="([^"]+)"/.exec(svg);
  const fill = /<path[^>]*\sfill="([^"]+)"/.exec(svg);
  if (!box || !path) return null;
  return { size: Math.max(Number(box[1]), Number(box[2])), path: path[1]!, fill: fill?.[1] ?? '#000000' };
}

/** Saves a blob under a file name through a temporary link. */
export function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
